"use client";

import { animate, motion, PanInfo, useMotionValue, useTransform } from "framer-motion";
import { Archive, Check as CheckIcon, Edit2, Ellipsis, Minus, Plus, Trash2, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { type ElementType, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { easeSmooth, springSnappy } from "@/lib/animations";
import { normalizeQuantityForDisplay } from "@/lib/categorization/utils";
import type { Ingredient } from "@/lib/types";
import { ingredientSchemas, translateValidationError, validate } from "@/lib/validation";

interface SwipeableIngredientCardProps {
  ingredient: Ingredient;
  isEditing: boolean;
  editText: string;
  onToggle: () => void;
  onDelete: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onEditTextChange: (text: string) => void;
  onIncrementQuantity?: () => void;
  onDecrementQuantity?: () => void;
  /** Custom icon component to show for the toggle (swipe right) action. Defaults to Archive. */
  toggleIcon?: ElementType;
  /** Callback when more actions button is clicked (mobile only) */
  onMoreActionsClick?: () => void;
}

export function SwipeableIngredientCard({
  ingredient,
  isEditing,
  editText,
  onToggle,
  onDelete,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onEditTextChange,
  onIncrementQuantity,
  onDecrementQuantity,
  toggleIcon,
  onMoreActionsClick,
}: SwipeableIngredientCardProps) {
  const tValidation = useTranslations("validation");
  const tIngredients = useTranslations("ingredients");
  const [isTouchDevice, setIsTouchDevice] = useState(false);
  const [isTouchTablet, setIsTouchTablet] = useState(false);
  const x = useMotionValue(0);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const swipeRef = useRef<HTMLDivElement>(null);
  const touchStartPos = useRef<{ x: number; y: number } | null>(null);
  const [dragDirection, setDragDirection] = useState<"horizontal" | "vertical" | null>(null);
  const ToggleIcon = toggleIcon ?? Archive;

  // Double tap detection for mobile
  const lastTapTime = useRef<number>(0);
  const lastTapX = useRef<number>(0);
  const DOUBLE_TAP_DELAY = 300; // ms

  // Detect touch device - prioritize touch capability and narrow screens
  useEffect(() => {
    const checkTouch = () => {
      const hasTouch = "ontouchstart" in window || navigator.maxTouchPoints > 0;
      const isMobileWidth = window.innerWidth < 768; // md breakpoint
      setIsTouchDevice(hasTouch && isMobileWidth);
      setIsTouchTablet(hasTouch && !isMobileWidth); // Touch device but larger than mobile
    };
    checkTouch();
    window.addEventListener("resize", checkTouch);
    return () => window.removeEventListener("resize", checkTouch);
  }, []);

  // Add non-passive touchmove listener to prevent scroll on horizontal intent
  useEffect(() => {
    const element = swipeRef.current;
    if (!element) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartPos.current || dragDirection === "vertical") return;

      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);

      // If horizontal movement is significant and at least as large as vertical, prevent scroll to allow swipe
      if (deltaX > 3 && deltaX >= deltaY) {
        e.preventDefault();
        setDragDirection("horizontal");
      }
    };

    const handleTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (t) {
        touchStartPos.current = { x: t.clientX, y: t.clientY };
        // reset drag direction when a new touch starts
        setDragDirection(null);
      }
    };

    element.addEventListener("touchmove", handleTouchMove, { passive: false });
    element.addEventListener("touchstart", handleTouchStart, { passive: true });

    return () => {
      element.removeEventListener("touchmove", handleTouchMove);
      element.removeEventListener("touchstart", handleTouchStart);
    };
  }, [dragDirection]);

  // Background color based on swipe direction - matches threshold distance
  const backgroundColor = useTransform(
    x,
    [-100, 0, 100],
    [
      "rgba(239, 68, 68, 1)", // Full red at threshold (delete)
      "rgba(0, 0, 0, 0)", // Transparent
      "rgba(34, 197, 94, 1)", // Full green at threshold (complete)
    ]
  );

  // Icon opacity and scale based on swipe distance - gradual from 15% threshold
  // For delete (swipe left): icon on the RIGHT side
  const deleteIconOpacity = useTransform(x, [-300, -100, -45, -15, 0], [1, 1, 0.8, 0.3, 0]);
  const deleteIconScale = useTransform(x, [-300, -100, -45, -15, 0], [1.3, 1.2, 1.0, 0.8, 0.6]);

  // For check (swipe right): icon on the LEFT side
  const checkIconOpacity = useTransform(x, [0, 15, 45, 100, 300], [0, 0.3, 0.8, 1, 1]);
  const checkIconScale = useTransform(x, [0, 15, 45, 100, 300], [0.6, 0.8, 1.0, 1.2, 1.3]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Validate before saving
      const validationResult = validate(ingredientSchemas.text, editText);
      if (!validationResult.success) {
        toast.error(translateValidationError(tValidation, validationResult.error));
        return;
      }
      onSaveEdit();
    }
    if (e.key === "Escape") onCancelEdit();
  };

  const handleSaveEdit = () => {
    // Validate before saving
    const validationResult = validate(ingredientSchemas.text, editText);
    if (!validationResult.success) {
      toast.error(translateValidationError(tValidation, validationResult.error));
      return;
    }
    onSaveEdit();
  };

  const handleDragStart = (event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Detect drag direction based on initial movement
    // Priority: ANY horizontal movement locks to horizontal (swipe takes precedence over scroll)
    if (touchStartPos.current && !dragDirection) {
      const deltaX = Math.abs(info.offset.x);
      const _deltaY = Math.abs(info.offset.y);

      // If ANY horizontal movement (even with vertical), lock to horizontal drag
      // This ensures swipe gestures work even when user's finger drifts vertically
      if (deltaX > 3) {
        setDragDirection("horizontal");
        setIsDragging(true);

        // Prevent scrolling by stopping event propagation
        if (event.cancelable) {
          event.preventDefault();
          event.stopPropagation();
        }
      }
      // // Only lock to vertical if movement is PURELY vertical (no horizontal component)
      // else if (deltaY > 5 && deltaX <= 3) {
      //   setDragDirection("vertical");
      //   // Don't set isDragging, let scroll happen
      //   return;
      // }
    }

    // If already locked to horizontal, prevent scroll
    if (dragDirection === "horizontal") {
      setIsDragging(true);
      if (event.cancelable) {
        event.preventDefault();
        event.stopPropagation();
      }
    }

    // Clear any existing long press timer
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    // Reset states
    setIsDragging(false);
    setDragDirection(null);

    // If this was a vertical scroll, don't process as swipe
    // if (dragDirection === "vertical") {
    //   animate(x, 0, springSnappy);
    //   return;
    // }

    const offset = info.offset.x;
    const velocity = info.velocity.x;

    // Get card width for animation, use fixed 100 pixel threshold
    const cardWidth = cardRef.current?.offsetWidth || 375;
    const thresholdDistance = 100;
    const thresholdVelocityMinDistance = 50;
    const thresholdVelocity = 60;

    // Determine if action should be triggered based on 100px threshold or high velocity
    const shouldDelete =
      offset < -thresholdDistance ||
      (offset < -thresholdVelocityMinDistance && velocity < -thresholdVelocity);
    const shouldToggle =
      offset > thresholdDistance ||
      (offset > thresholdVelocityMinDistance && velocity > thresholdVelocity);

    if (shouldDelete) {
      // Vibrate if available (haptic feedback)
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Animate all the way left smoothly before deleting
      animate(x, -cardWidth * 1.2, easeSmooth).then(() => {
        onDelete();
      });
    } else if (shouldToggle) {
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      // Animate all the way right smoothly before toggling
      animate(x, cardWidth * 1.2, easeSmooth).then(() => {
        onToggle();
      });
    } else {
      // Didn't meet threshold - smoothly animate back to center (cancel)
      animate(x, 0, springSnappy);
    }
  };

  const handlePressStart = (e: React.PointerEvent<HTMLDivElement>) => {
    // Capture touch start position
    touchStartPos.current = { x: e.clientX, y: e.clientY };

    // Reset drag direction at the start of new gesture
    setDragDirection(null);

    // Try to capture the pointer so subsequent pointermove events are routed to this element
    try {
      // some browsers may not support setPointerCapture on the currentTarget
      (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
    } catch {
      // ignore
    }

    if (!isEditing && !isDragging) {
      longPressTimer.current = setTimeout(() => {
        onStartEdit();
      }, 500); // 500ms long press
    }
  };

  const handlePressEnd = (e?: React.PointerEvent<HTMLDivElement>) => {
    touchStartPos.current = null;
    setDragDirection(null);
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    // Release pointer capture if present
    try {
      if (e && (e.currentTarget as Element).releasePointerCapture) {
        (e.currentTarget as Element).releasePointerCapture(e.pointerId);
      }
    } catch {
      // ignore
    }
  };

  const handleTap = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only handle taps on mobile (touch devices)
    if (!isTouchDevice || isEditing || isDragging) return;

    const now = Date.now();
    const timeSinceLastTap = now - lastTapTime.current;
    const tapX = e.clientX;

    // Check if this is a double tap (within DOUBLE_TAP_DELAY ms and at similar position)
    if (timeSinceLastTap < DOUBLE_TAP_DELAY && Math.abs(tapX - lastTapX.current) < 50) {
      // Double tap detected - determine if it's left or right side
      const cardElement = cardRef.current;
      if (!cardElement) return;

      const rect = cardElement.getBoundingClientRect();
      const relativeX = tapX - rect.left;
      const cardWidth = rect.width;
      const percentPosition = (relativeX / cardWidth) * 100;

      // Left side (0-50%) = decrement, Right side (51-100%) = increment
      if (percentPosition <= 50 && onDecrementQuantity) {
        onDecrementQuantity();
        // Vibrate if available (haptic feedback)
        if (navigator.vibrate) {
          navigator.vibrate(30);
        }
      } else if (percentPosition > 50 && onIncrementQuantity) {
        onIncrementQuantity();
        // Vibrate if available (haptic feedback)
        if (navigator.vibrate) {
          navigator.vibrate(30);
        }
      }

      // Reset double tap tracking
      lastTapTime.current = 0;
      lastTapX.current = 0;
    } else {
      // First tap or too long since last tap - record this tap
      lastTapTime.current = now;
      lastTapX.current = tapX;
    }
  };

  // Don't show swipe on desktop or when editing
  if (!isTouchDevice || isEditing) {
    return (
      <div className="flex items-center gap-3 p-4 bg-input-bg border border-input-border rounded-lg group">
        {isEditing ? (
          <>
            <Input
              value={editText}
              onChange={(e) => onEditTextChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1"
              autoFocus
              maxLength={200}
            />
            <div className="flex items-center gap-1">
              <button
                onClick={handleSaveEdit}
                className="p-1 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                title={tIngredients("save")}
                aria-label={tIngredients("save")}
              >
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                onClick={onCancelEdit}
                className="p-1 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                title={tIngredients("cancel")}
                aria-label={tIngredients("cancel")}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </>
        ) : (
          <>
            <Checkbox checked={ingredient.completed} onCheckedChange={onToggle} />
            <span
              className={
                ingredient.completed ? "flex-1 line-through text-muted-foreground" : "flex-1"
              }
            >
              {ingredient.quantity && (
                <>
                  <span className="text-primary font-semibold">
                    {normalizeQuantityForDisplay(ingredient.quantity)}
                  </span>{" "}
                </>
              )}
              {ingredient.text}
            </span>
            <div
              className={`flex items-center gap-1 transition-opacity ${isTouchTablet ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
            >
              {onDecrementQuantity && (
                <button
                  onClick={onDecrementQuantity}
                  className="p-1 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                  title={tIngredients("decrementQuantity")}
                  aria-label={tIngredients("decrementQuantity")}
                >
                  <Minus className="w-5 h-5" />
                </button>
              )}
              {onIncrementQuantity && (
                <button
                  onClick={onIncrementQuantity}
                  className="p-1 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                  title={tIngredients("incrementQuantity")}
                  aria-label={tIngredients("incrementQuantity")}
                >
                  <Plus className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={onStartEdit}
                className="p-1 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
                title={tIngredients("edit")}
                aria-label={tIngredients("edit")}
              >
                <Edit2 className="w-5 h-5" />
              </button>
              <button
                onClick={onDelete}
                className="p-1 text-muted-foreground hover:text-destructive transition-colors cursor-pointer"
                title={tIngredients("delete")}
                aria-label={tIngredients("delete")}
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // Mobile swipeable version
  return (
    <div className="relative rounded-lg" ref={cardRef}>
      {/* Background with action indicators */}
      <motion.div
        className="absolute inset-0 flex items-center justify-between px-4 pointer-events-none rounded-lg overflow-hidden"
        style={{ backgroundColor }}
      >
        {/* Check icon (LEFT side - for swipe right to complete) */}
        <motion.div
          className="flex items-center justify-center"
          style={{ opacity: checkIconOpacity, scale: checkIconScale }}
        >
          {/* Allow caller to override the toggle icon; default to Archive */}
          <ToggleIcon className="w-5 h-5 text-white drop-shadow-lg" strokeWidth={1.5} />
        </motion.div>

        {/* Delete icon (RIGHT side - for swipe left to delete) */}
        <motion.div
          className="flex items-center justify-center"
          style={{ opacity: deleteIconOpacity, scale: deleteIconScale }}
        >
          <Trash2 className="w-5 h-5 text-white drop-shadow-lg" strokeWidth={1.5} />
        </motion.div>
      </motion.div>

      {/* Swipeable card content */}
      <motion.div
        className={`flex items-center gap-3 px-4 py-3 bg-input-bg border border-input-border rounded-lg relative ${
          dragDirection === "horizontal" || isDragging ? "touch-none" : "touch-pan-y"
        }`}
        style={{
          x,
        }}
        drag={dragDirection === "vertical" ? false : "x"}
        dragConstraints={{ left: -250, right: 250 }}
        dragElastic={{ left: 0.2, right: 0.2 }}
        dragMomentum={false}
        dragDirectionLock={true}
        dragPropagation={false}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onPointerDown={handlePressStart}
        onPointerUp={handlePressEnd}
        onPointerCancel={handlePressEnd}
        onClick={handleTap}
        ref={swipeRef}
      >
        <span
          className={
            ingredient.completed
              ? "flex-1 line-through text-muted-foreground break-words overflow-hidden"
              : "flex-1 break-words overflow-hidden"
          }
        >
          {ingredient.quantity && (
            <>
              <span className="text-primary font-semibold">
                {normalizeQuantityForDisplay(ingredient.quantity)}
              </span>{" "}
            </>
          )}
          {ingredient.text}
        </span>
        {/* More actions button for mobile */}
        {onMoreActionsClick && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onMoreActionsClick();
            }}
            className="p-1 text-muted-foreground hover:text-primary transition-colors cursor-pointer"
            title={tIngredients("moreActions")}
            aria-label={tIngredients("moreActions")}
          >
            <Ellipsis className="w-5 h-5" />
          </button>
        )}
      </motion.div>
    </div>
  );
}
